import React, { useState, useEffect, useContext, useRef } from "react";
import CustomDataTable from "../../../components/CustomDataTable";
import Loader from "../../../components/Loader";
import { FaEdit} from "react-icons/fa";
import { MdModeEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import CustomToolTip from "../../../components/ToolTip";
import { toast } from "react-toastify";
import Hoc from "../../../components/HOC";
import { UserContext } from "../../../Context/User";
import CustomDropdown from "../../../components/CustomDropdown";

const ExecutiveTable = () => {
  const {
    usersList,
    fetchUsersList,
    userLoading,
    userError,
    updateUserStatus,
    totalUsers,
    totalPages
  } = useContext(UserContext);

  // Pagination states - using backend defaults
  const [page, setPage] = useState(0); // Backend uses 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(10); // Default size
  const [currentPage, setCurrentPage] = useState(1); // 1-indexed for display

  // Filter states - these will be sent to backend
  const [statusFilter, setStatusFilter] = useState("A");
  const [userTypeFilter, setUserTypeFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();

  const [isSearching, setIsSearching] = useState(false);
  const searchDebounceRef = useRef();

  // Get filters for API request
  const getFilters = () => {
    const filters = {};
    if (statusFilter) filters.stsCd = statusFilter;
    
    // Try multiple possible account type parameter names
    if (userTypeFilter) {
      filters.acctType = userTypeFilter;
    }
    
    if (searchQuery) filters.search = searchQuery;
    
    return filters;
  };

  const handleEdit = (row) => {
    setStatusFilter("A");
    navigate(`/edituser/${row.id}`, { state: { activeItem: "Employee" } });
  };

  const handleClick = () => {
    setStatusFilter("A");
    navigate("/addemployee", { state: { activeItem: "Employee" } });
  };

  // Handle pagination change
  const handleChangePage = (newPage) => {
    setPage(newPage - 1); // Convert 1-indexed to 0-indexed for backend
    setCurrentPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (rowsPerPage) => {
    setRowsPerPage(rowsPerPage);
    setPage(0); // Reset to first page when changing rows per page
    setCurrentPage(1);
  };

  // Calculate pagination info
  const getPaginationInfo = () => {
    if (totalUsers === 0) return { start: 0, end: 0 };
    const start = page * rowsPerPage + 1;
    const end = Math.min((page + 1) * rowsPerPage, totalUsers);
    return { start, end };
  };

  // Generate array of page numbers to display
  const getPageNumbers = () => {
    const totalPageCount = Math.ceil(totalUsers / rowsPerPage);
    let pages = [];
    
    // Simple case: show all pages if less than 5
    if (totalPageCount <= 5) {
      for (let i = 1; i <= totalPageCount; i++) {
        pages.push(i);
      }
      return pages;
    }
    
    // More complex case: show current page and some pages around it
    if (currentPage <= 3) {
      // Near start
      return [1, 2, 3, 4, 5];
    } else if (currentPage >= totalPageCount - 2) {
      // Near end
      return [
        totalPageCount - 4,
        totalPageCount - 3,
        totalPageCount - 2,
        totalPageCount - 1,
        totalPageCount
      ];
    } else {
      // Middle
      return [
        currentPage - 2,
        currentPage - 1,
        currentPage,
        currentPage + 1,
        currentPage + 2
      ];
    }
  };

  // Debounced API call for search
  useEffect(() => {
    setIsSearching(true);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const filters = getFilters();
      fetchUsersList(page, rowsPerPage, filters)
        .then(data => {
          // No toast for 404/empty
        })
        .catch(error => {
          if (
            error?.message &&
            (error.message.includes("404") || error.message.toLowerCase().includes("no users found"))
          ) {
            toast.error("No users found for the given criteria.");
          } else if (error?.message) {
            toast.error(error?.message || "Failed to fetch users");
          }
        })
        .finally(() => {
          setIsSearching(false);
        });
    }, 500);
    return () => clearTimeout(searchDebounceRef.current);
  }, [page, rowsPerPage, statusFilter, userTypeFilter, searchQuery, fetchUsersList]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
    setCurrentPage(1);
  }, [statusFilter, userTypeFilter, searchQuery]);

  // Modify the error effect to prevent showing 403 and 404/no users found errors
  useEffect(() => {
    if (
      userError &&
      (!userError.includes('403') && !usersList.length) &&
      !(userError.includes('404') || userError.toLowerCase().includes('no users found'))
    ) {
      toast.error(userError);
    }
  }, [userError, usersList]);

  // After returning from add/edit, always reset to show only active users
  useEffect(() => {
    setStatusFilter("A");
  }, []);

  // Get pagination info
  const { start, end } = getPaginationInfo();
  const pageNumbers = getPageNumbers();
  const totalPageCount = Math.ceil(totalUsers / rowsPerPage);
  
  // Add a callback to be called after successful add/edit
  const handleAfterSubmit = () => {
    setStatusFilter("A");
    fetchUsersList(0, rowsPerPage, { stsCd: "A" });
  };

  return (
    <div className="bg-white">
      <Loader isLoading={userLoading || isSearching} />

      <div className="p-6  mx-auto bg-white">
        <div className="overflow-hidden bg-white border border-gray-200 rounded-lg">
          {/* Search and filters inside the table container */}
          <div className="p-2 sm:p-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-4">
              <div className="relative min-w-0 flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-search h-5 w-5 text-gray-400">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.3-4.3"></path>
                  </svg>
                </div>
                <input
                  type="text"
                  className="block w-full h-[38px] pl-10 pr-4 py-0 rounded-[4px] border border-[#D1D1D1] bg-white text-sm text-[#201F1E] placeholder-[#656464] focus:outline-none transition-colors duration-150"
                  placeholder="Search users by name"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="flex flex-wrap sm:flex-nowrap items-end gap-2 w-full sm:w-auto sm:shrink-0">
                <CustomDropdown
                  value={[
                    { value: "", label: "All Roles" },
                    { value: "ADMIN", label: "Admin" },
                    { value: "MKTG_EXEC", label: "Marketing Executive" },
                    { value: "RECRUITER", label: "Recruiter" },
                  ].find((option) => option.value === userTypeFilter)}
                  name="userTypeFilter"
                  handleChange={(option) => setUserTypeFilter(option?.value || "")}
                  placeholder="Role"
                  options={[
                    { value: "", label: "All Roles" },
                    { value: "ADMIN", label: "Admin" },
                    { value: "MKTG_EXEC", label: "Marketing Executive" },
                    { value: "RECRUITER", label: "Recruiter" },
                  ]}
                  handleGetOptionValue={(opt) => opt.value}
                  handleGetOptionName={(opt) => opt.label}
                  customClasses="w-full min-[480px]:w-52 !mt-0"
                />
                <CustomDropdown
                  value={[
                    { value: "", label: "All Status" },
                    { value: "A", label: "Active" },
                    { value: "D", label: "Deactive" },
                  ].find((option) => option.value === statusFilter)}
                  name="statusFilter"
                  handleChange={(option) => setStatusFilter(option?.value || "")}
                  placeholder="Status"
                  options={[
                    { value: "", label: "All Status" },
                    { value: "A", label: "Active" },
                    { value: "D", label: "Deactive" },
                  ]}
                  handleGetOptionValue={(opt) => opt.value}
                  handleGetOptionName={(opt) => opt.label}
                  customClasses="w-full min-[480px]:w-40 !mt-0"
                />
                <button
                  type="button"
                  onClick={handleClick}
                  className="h-[38px] px-4 bg-[#201F1E] hover:bg-[#201F1E] text-white text-sm font-medium rounded-[4px] transition-colors flex items-center justify-center whitespace-nowrap shrink-0"
                >
                  Add User
                </button>
              </div>
            </div>
          </div>
          {/* Table with sticky header */}
          <div className="overflow-y-auto" style={{ maxHeight: "68vh" }}>
            <table className="min-w-full divide-y divide-gray-200 table-fixed">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                    Candidate
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
                    Job Title
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
                    Phone Number
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {usersList.map((user) => {
                  const statusText = user.stsCd === "A" ? "Active" : "Deactive";
                  const statusStyles = user.stsCd === "A" 
                    ? "bg-green-100 text-green-600" 
                    : "bg-red-100 text-red-600";
                  const jobTitle = user.acctType === "ADMIN" ? "Admin" : user.acctType === "MKTG_EXEC" ? "Marketing Executive" : "Recruiter";
                  const department = user.acctType === "ADMIN" ? "Administration" : user.acctType === "MKTG_EXEC" ? "Marketing" : "Recruitment";
                  const firstInitial = user?.firstName?.charAt(0)?.toUpperCase() || "U";
                  const lastInitial = user?.lastName?.charAt(0)?.toUpperCase() || "";
                  const initials = `${firstInitial}${lastInitial}`;

                  return (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-100 text-[#201F1E] flex items-center justify-center font-semibold">
                              {initials}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-[#656464]">{user.firstName} {user.lastName}</div>
                            <div className="text-sm text-[#656464]">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-normal text-[#656464]">{jobTitle}</div>
                        <div className="text-xs text-[#656464]">{department}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-[#656464]">{user.phone || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.stsCd === "A" ? "bg-[#34D39920] text-[#34D399]" : "bg-red-50 text-red-400"}`}>
                          {statusText}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEdit(user)}
                          className="text-[#201F1E] hover:text-[#201F1E]"
                        >
                          <FaEdit size={20} data-tip data-for={`editTip-${user.id}`} />
                          <CustomToolTip id={`editTip-${user.id}`} title="Edit" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        
          {/* Pagination */}
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button 
                onClick={() => currentPage > 1 && handleChangePage(1)}
                disabled={currentPage === 1 || totalUsers === 0}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button 
                onClick={() => currentPage > 1 && handleChangePage(currentPage - 1)}
                disabled={currentPage === 1 || totalUsers === 0}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => currentPage < totalPageCount && handleChangePage(currentPage + 1)}
                disabled={currentPage === totalPageCount || totalUsers === 0}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => currentPage < totalPageCount && handleChangePage(totalPageCount)}
                disabled={currentPage === totalPageCount || totalUsers === 0}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{start}</span> to <span className="font-medium">{end}</span> of <span className="font-medium">{totalUsers}</span> users
                </p>
              </div>
              <div className="flex items-center space-x-4">
              
                
                  <div className="flex items-center space-x-2">
                          <label htmlFor="rows-per-page" className="text-sm text-gray-700">
                            Rows per page:
                          </label>
                          <select
                            id="rows-per-page"
                            value={rowsPerPage}
                            onChange={e => handleRowsPerPageChange(Number(e.target.value))}
                            className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                          </select>
                        </div>
            
                
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => currentPage > 1 && handleChangePage(1)}
                    disabled={currentPage === 1 || totalUsers === 0}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    First
                  </button>
                  <button
                    onClick={() => currentPage > 1 && handleChangePage(currentPage - 1)}
                    disabled={currentPage === 1 || totalUsers === 0}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {pageNumbers.map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => handleChangePage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 ${
                        pageNum === currentPage
                          ? 'bg-blue-50 text-blue-600'
                          : 'bg-white text-gray-700'
                      } text-sm font-medium hover:bg-gray-50`}
                    >
                      {pageNum}
                    </button>
                  ))}
                  <button
                    onClick={() => currentPage < totalPageCount && handleChangePage(currentPage + 1)}
                    disabled={currentPage === totalPageCount || totalUsers === 0}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => currentPage < totalPageCount && handleChangePage(totalPageCount)}
                    disabled={currentPage === totalPageCount || totalUsers === 0}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Last
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hoc(ExecutiveTable);
